import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from '../hooks/useInView';
import { CheckCircle, Award } from 'lucide-react';
import Logo from './Logo';

export default function About() {
  const [ref, inView] = useInView({ threshold: 0.2 });

  const highlights = [
    'VCA** gecertificeerd',
    'ISO 9001 kwaliteitsmanagement',
    'Ervaren en gekwalificeerd personeel',
    'Landelijke dekking',
    '24/7 beschikbaarheid',
    'Duurzame werkwijze',
  ];

  return (
    <section id="about" ref={ref} className="section-padding relative overflow-hidden">
      {/* Background accent */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-ferry-crimson/30 to-transparent" />

      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Image/Visual */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="relative"
          >
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-ferry-crimson/20 via-ferry-dark to-ferry-gray" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-24 h-24 mx-auto mb-6 flex items-center justify-center">
                    <Logo size={96} />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">FERRY INFRA</h3>
                  <p className="text-ferry-cream text-sm italic">Veilig werken aan de energie van morgen</p>
                </div>
              </div>
              {/* Decorative grid lines */}
              <div className="absolute inset-0 opacity-10"
                style={{
                  backgroundImage: 'linear-gradient(rgba(155,35,53,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(155,35,53,0.3) 1px, transparent 1px)',
                  backgroundSize: '40px 40px',
                }}
              />
            </div>

            {/* Floating stats cards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="absolute -bottom-6 -right-6 glass-card p-4 flex items-center gap-3"
            >
              <div className="w-10 h-10 rounded-lg bg-ferry-crimson/20 flex items-center justify-center">
                <Award size={20} className="text-ferry-crimson" />
              </div>
              <div>
                <p className="text-white font-bold text-lg">VCA**</p>
                <p className="text-ferry-gray-text text-xs">Gecertificeerd</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Right - Content */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <span className="text-ferry-crimson font-semibold text-sm uppercase tracking-widest">Over Ons</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4 mb-6 leading-tight">
              Uw betrouwbare partner in{' '}
              <span className="gradient-text">infrastructuur</span>
            </h2>
            <p className="text-ferry-gray-text leading-relaxed mb-6">
              Ferry Infra is een toonaangevend bedrijf in de infrastructuursector, gespecialiseerd in 
              werkzaamheden voor de energietransitie. Met een team van ervaren professionals leveren 
              wij hoogwaardige diensten met een onwrikbare focus op veiligheid en kwaliteit.
            </p>
            <p className="text-ferry-gray-text leading-relaxed mb-8">
              Onze missie is helder: veilig werken aan de energie van morgen. Wij geloven dat de 
              energietransitie alleen slaagt met vakmanschap, innovatie en een sterke veiligheidscultuur.
            </p>

            {/* Highlights Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {highlights.map((item, i) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, x: 20 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.4 + i * 0.08 }}
                  className="flex items-center gap-3 py-2"
                >
                  <CheckCircle size={18} className="text-ferry-crimson flex-shrink-0" />
                  <span className="text-sm text-white/80">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
